// F3/FR-SH5a: the viewer-side gate for a protected share link — rendered by SharePage,
// WatchPage (`?share=`) and EmbedPage whenever the watch/share fetch comes back 401
// `share_locked`. Built on @grackle/ui (Card, Icon) and the Field/Button adapters in
// components/ui.tsx.
import { useState, type FormEvent } from "react";
import { Card, Icon } from "@grackle/ui";
import { api } from "../api/client";
import type { ShareEmailChallengeOut, ShareUnlockOut } from "../api/types";
import { Button, Field } from "../components/ui";
import { gateErrorMessage, nextGateStep, unlockHeaders, type ShareGateInfo } from "./shareGateLogic";
import "./watch.css";

function PasswordGate({
  token,
  unlockToken,
  onUnlocked,
}: {
  token: string;
  unlockToken: string | null;
  onUnlocked: (unlockToken: string) => void;
}) {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!password || busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api<ShareUnlockOut>(
        "POST",
        `/share/${encodeURIComponent(token)}/unlock`,
        { password },
        unlockHeaders(unlockToken),
      );
      onUnlocked(res.unlock_token);
    } catch (err) {
      setError(gateErrorMessage(err, "invalid_password", "Incorrect password."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit}>
      <Field
        label="Password"
        type="password"
        autoFocus
        autoComplete="off"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        error={error ?? undefined}
        disabled={busy}
      />
      <Button type="submit" block disabled={!password || busy} loading={busy}>
        {busy ? "Checking…" : "Unlock"}
      </Button>
    </form>
  );
}

function EmailGate({
  token,
  unlockToken,
  domainRestricted,
  onUnlocked,
}: {
  token: string;
  unlockToken: string | null;
  domainRestricted: boolean;
  onUnlocked: (unlockToken: string) => void;
}) {
  const [stage, setStage] = useState<"enter-email" | "enter-code">("enter-email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [challenge, setChallenge] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function requestCode(e: FormEvent) {
    e.preventDefault();
    if (!email || busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api<ShareEmailChallengeOut>(
        "POST",
        `/share/${encodeURIComponent(token)}/email`,
        { email },
        unlockHeaders(unlockToken),
      );
      setChallenge(res.challenge);
      setStage("enter-code");
    } catch (err) {
      setError(gateErrorMessage(err, "email_not_allowed", "That email isn't allowed for this link."));
    } finally {
      setBusy(false);
    }
  }

  async function verifyCode(e: FormEvent) {
    e.preventDefault();
    if (!challenge || !code || busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api<ShareUnlockOut>(
        "POST",
        `/share/${encodeURIComponent(token)}/email/verify`,
        { challenge, code },
        unlockHeaders(unlockToken),
      );
      onUnlocked(res.unlock_token);
    } catch (err) {
      setError(gateErrorMessage(err, "invalid_code", "That code didn't match. Try again."));
    } finally {
      setBusy(false);
    }
  }

  if (stage === "enter-code") {
    return (
      <form onSubmit={verifyCode}>
        <div className="gk-sm">We sent a 6-digit code to {email}.</div>
        <Field
          label="Code"
          inputMode="numeric"
          autoComplete="one-time-code"
          autoFocus
          maxLength={6}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
          error={error ?? undefined}
          disabled={busy}
        />
        <Button type="submit" block disabled={code.length !== 6 || busy} loading={busy}>
          {busy ? "Checking…" : "Verify"}
        </Button>
        <Button type="button" variant="ghost" block
          onClick={() => {
            setStage("enter-email");
            setCode("");
            setError(null);
          }}
          disabled={busy}
        >
          Use a different email
        </Button>
      </form>
    );
  }

  return (
    <form onSubmit={requestCode}>
      <div className="gk-sm">
        {domainRestricted
          ? "Enter your email to get a code. This link only allows certain email domains."
          : "Enter your email to get a 6-digit code."}
      </div>
      <Field
        label="Email"
        type="email"
        autoFocus
        autoComplete="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        error={error ?? undefined}
        disabled={busy}
      />
      <Button type="submit" block disabled={!email || busy} loading={busy}>
        {busy ? "Sending…" : "Send code"}
      </Button>
    </form>
  );
}

export default function ShareGate({
  token,
  gate,
  unlockToken,
  onUnlocked,
}: {
  token: string;
  gate: ShareGateInfo;
  unlockToken: string | null;
  onUnlocked: (unlockToken: string) => void;
}) {
  const step = nextGateStep(gate);
  return (
    <Card size="lg" className="share-gate-panel">
      <Icon name={step === "email" ? "envelope-simple" : "lock-simple"} size={28} />
      <h2>{step === "email" ? "Verify your email" : "This link is protected"}</h2>
      {step === "password" ? (
        <PasswordGate token={token} unlockToken={unlockToken} onUnlocked={onUnlocked} />
      ) : step === "email" ? (
        <EmailGate token={token} unlockToken={unlockToken} domainRestricted={gate.domainRestricted} onUnlocked={onUnlocked} />
      ) : null}
    </Card>
  );
}
